import { useState } from "react";
import { Button } from "./ui/Button";
import type { JobManualCreateRequest } from "../types";

interface Props {
  initialUrl?: string;
  onSubmit: (data: JobManualCreateRequest) => Promise<void>;
  loading: boolean;
  onCancel?: () => void;
}

const inputClass =
  "w-full px-3 py-2.5 border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 text-sm focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-colors duration-200";

const labelClass = "block mb-1.5 text-sm font-medium text-slate-700 dark:text-slate-300";

export default function ManualJobEntryForm({ initialUrl = "", onSubmit, loading, onCancel }: Props) {
  const [sourceUrl, setSourceUrl] = useState(initialUrl);
  const [companyName, setCompanyName] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [jdContent, setJdContent] = useState("");
  const [contactInfo, setContactInfo] = useState("");
  const [postingDate, setPostingDate] = useState("");
  const [deadlineDate, setDeadlineDate] = useState("");
  const [submissionLink, setSubmissionLink] = useState("");

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    await onSubmit({
      source_url: sourceUrl.trim() || undefined,
      company_name: companyName || undefined,
      job_title: jobTitle || undefined,
      jd_content: jdContent || undefined,
      contact_info: contactInfo || undefined,
      posting_date: postingDate || undefined,
      deadline_date: deadlineDate || undefined,
      submission_link: submissionLink || undefined,
    });
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-5">
      <div>
        <label className={labelClass}>Job Posting URL</label>
        <input
          type="url"
          value={sourceUrl}
          onChange={(e) => setSourceUrl(e.target.value)}
          placeholder="https://example.com/jobs/..."
          className={inputClass}
        />
      </div>

      <div>
        <label className={labelClass}>Company Name</label>
        <input
          type="text"
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          placeholder="Acme Corp"
          className={inputClass}
        />
      </div>

      <div>
        <label className={labelClass}>Job Title</label>
        <input
          type="text"
          value={jobTitle}
          onChange={(e) => setJobTitle(e.target.value)}
          placeholder="Software Engineer"
          className={inputClass}
        />
      </div>

      <div>
        <label className={labelClass}>
          Job Description (Markdown) <span className="text-error">*</span>
        </label>
        <textarea
          value={jdContent}
          onChange={(e) => setJdContent(e.target.value)}
          rows={12}
          placeholder={"# Job Description\n\nPaste the full job description here..."}
          className={`${inputClass} resize-y font-mono`}
        />
      </div>

      <div>
        <label className={labelClass}>Contact Info (Markdown)</label>
        <textarea
          value={contactInfo}
          onChange={(e) => setContactInfo(e.target.value)}
          rows={3}
          placeholder="Recruiter name, email..."
          className={`${inputClass} resize-y font-mono`}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Posting Date</label>
          <input
            type="date"
            value={postingDate}
            onChange={(e) => setPostingDate(e.target.value)}
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Application Deadline</label>
          <input
            type="date"
            value={deadlineDate}
            onChange={(e) => setDeadlineDate(e.target.value)}
            className={inputClass}
          />
        </div>
      </div>

      <div>
        <label className={labelClass}>Submission Link</label>
        <input
          type="url"
          value={submissionLink}
          onChange={(e) => setSubmissionLink(e.target.value)}
          placeholder="https://example.com/apply/..."
          className={inputClass}
        />
      </div>

      <div className="flex gap-3">
        <Button
          type="submit"
          variant="primary"
          loading={loading}
          disabled={loading || !jdContent.trim()}
        >
          {loading ? "Saving..." : "Save Job"}
        </Button>
        {onCancel && (
          <Button type="button" variant="secondary" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
}
